import { layers, namedFlavor } from '@protomaps/basemaps'
import type { LayerSpecification } from '@maplibre/maplibre-gl-style-spec'
import type { StyleSpecification } from 'maplibre-gl'
import { getMapTileSettings, type MapTileSettings } from './runtimeConfig'

/**
 * Tile Configuration — per-instance (P-8a).
 *
 * Exactly one of two sources, chosen per customer. Web: at BUILD time (Vite bakes VITE_* vars
 * into the bundle, and every box builds its own images from its own .env, so a per-customer key
 * already yields a per-customer bundle):
 *   VITE_PROTOMAPS_API_KEY  hosted Protomaps CDN
 *   VITE_PMTILES_URL        an https URL to a self-hosted .pmtiles archive
 * Native (one store binary for every customer): the same two values from the instance's
 * `GET /api/config/public` `map` block, cached per instance (lib/runtimeConfig.ts).
 *
 * The style itself never names the real host: tiles, glyphs and sprite all read through the
 * `mapcache://` protocol (lib/mapCacheProtocol.ts, registered once in providers/AppProviders.tsx),
 * which serves from the offline map cache keyed by tile position / asset name and only then asks
 * the configured upstream (buildTileUpstream / ASSET_UPSTREAM). Switching source is config-only
 * (spec §4.3 of 2026-10-23-a3-map-follow-ups-design.md).
 */

const CDN_TEMPLATE = 'https://api.protomaps.com/tiles/v4/{z}/{x}/{y}.mvt'
const ATTRIBUTION =
  '<a href="https://protomaps.com">Protomaps</a> | <a href="https://openstreetmap.org/copyright">© OpenStreetMap</a>'
const PLACEHOLDER_KEY = 'your_api_key_here'

/** Glyph + sprite upstream. Self-hosting these later = change this one value. */
export const ASSET_UPSTREAM = 'https://protomaps.github.io/basemaps-assets'

/** Where tiles actually come from, behind the mapcache:// layer. */
export type TileUpstream =
  | { kind: 'hosted'; template: string }
  | { kind: 'pmtiles'; url: string }
  | { kind: 'none' }

/**
 * Pure: takes the two per-instance settings and returns the tile upstream.
 * PMTiles wins when both are set (warns); neither (or the placeholder key) = none (warns).
 */
export function buildTileUpstream(opts: { apiKey?: string; pmtilesUrl?: string }): TileUpstream {
  const key = opts.apiKey && opts.apiKey !== PLACEHOLDER_KEY ? opts.apiKey : ''
  const pmtiles = opts.pmtilesUrl ?? ''

  if (pmtiles && key) {
    console.warn(
      'Both VITE_PMTILES_URL and VITE_PROTOMAPS_API_KEY are set. Using the self-hosted ' +
        'PMTiles archive and ignoring the hosted key. Set exactly one per instance.'
    )
  }
  if (pmtiles) return { kind: 'pmtiles', url: pmtiles }
  if (!key) {
    console.warn(
      'No basemap tile source configured. Set VITE_PROTOMAPS_API_KEY (free at ' +
        'https://protomaps.com/dashboard) or VITE_PMTILES_URL. Maps will render blank.'
    )
    return { kind: 'none' }
  }
  return { kind: 'hosted', template: `${CDN_TEMPLATE}?key=${key}` }
}

type BasemapSource = {
  type: 'vector'
  tiles: string[]
  minzoom: number
  maxzoom: number
  attribution: string
}

/**
 * The MapLibre source spec. Source-independent by design: the tile setting is resolved by
 * buildTileUpstream behind mapcache://, so the same spec serves hosted and PMTiles — it takes
 * no tile setting at all.
 */
export function buildBasemapSource(): BasemapSource {
  return {
    type: 'vector',
    tiles: ['mapcache://tile/{z}/{x}/{y}'],
    minzoom: 0,
    maxzoom: 15,
    attribution: ATTRIBUTION,
  }
}

// Basemap labels are tuned for canvassing: streets + light orientation context
// (neighborhood/city/water names), NOT a points-of-interest directory. We drop
// these layers from the Protomaps "light" flavor so businesses don't clutter the
// map — the address dots stay the visual priority.
const SUPPRESSED_LAYERS = new Set([
  'pois',          // every shop/restaurant/amenity icon + label — the main source of clutter
  'roads_oneway',  // one-way arrow icons — noise for door-to-door canvassing
])

/** Label font for MapLibre symbol layers. The Protomaps glyph host serves ONLY the Noto Sans family
 *  (map audit 2026-10-09: 'Open Sans Semibold' 404s, so labels silently vanished). Guarded by
 *  lib/mapFonts.guard.test.ts. */
export const MAP_LABEL_FONT = ['Noto Sans Medium']

// MapLibre style specification using Protomaps basemap.
// Fonts (glyphs) and icons (sprite) come from Protomaps' official asset host (ASSET_UPSTREAM)
// via mapcache://; without them, place/street labels and shields can't render.
function buildMapStyle(): StyleSpecification {
  const baseLayers = layers('protomaps', namedFlavor('light'), { lang: 'en' })
  return {
    version: 8,
    glyphs: 'mapcache://glyph/{fontstack}/{range}',
    sprite: 'mapcache://sprite/light',
    sources: {
      protomaps: buildBasemapSource(),
    },
    layers: baseLayers
      .filter((layer: LayerSpecification) => !SUPPRESSED_LAYERS.has(layer.id))
      .map((layer: LayerSpecification) => {
        // Show house numbers earlier (default z18 → z16) — so canvassers don't have to zoom way in
        if (layer.id === 'address_label') return { ...layer, minzoom: 16 }
        // Show minor road labels earlier (default z15 → z14)
        if (layer.id === 'roads_labels_minor') return { ...layer, minzoom: 14 }
        return layer
      }),
  }
}

/**
 * [Native B1] Tile settings are read at RUNTIME (lib/runtimeConfig.ts — web: the build's VITE_*
 * env; native: the instance's cached `GET /api/config/public`), never captured at module load:
 * on native the instance and its config arrive after the bundle is evaluated. Both getters are
 * memoized on the settings object's identity, so maps keep a stable `mapStyle` reference (no
 * style reload per render) and buildTileUpstream warns once per settings change, not per tile.
 */
let styleMemo: { settings: MapTileSettings; style: StyleSpecification } | null = null
let upstreamMemo: { settings: MapTileSettings; upstream: TileUpstream } | null = null

/** The basemap style for every map in the app. */
export function getMapStyle(): StyleSpecification {
  const settings = getMapTileSettings()
  if (styleMemo?.settings !== settings) styleMemo = { settings, style: buildMapStyle() }
  return styleMemo.style
}

/** The tile upstream behind mapcache:// for this instance's current settings. */
export function getTileUpstream(): TileUpstream {
  const settings = getMapTileSettings()
  if (upstreamMemo?.settings !== settings) upstreamMemo = { settings, upstream: buildTileUpstream(settings) }
  return upstreamMemo.upstream
}

// Default view state (continental US center)
export const DEFAULT_VIEW_STATE = {
  longitude: -98.5795,
  latitude: 39.8283,
  zoom: 4,
  pitch: 0,
  bearing: 0,
}

// Layer and source ID constants for feature layers
export const LAYER_IDS = {
  BOUNDARIES_FILL: 'turf-boundaries-fill',
  BOUNDARIES_LINE: 'turf-boundaries-line',
  CONTACTS_CLUSTERS: 'contact-clusters',
  CONTACTS_CLUSTER_COUNT: 'contact-cluster-count',
  CONTACTS_UNCLUSTERED: 'contacts-unclustered',
  WORKPLACES_UNCLUSTERED: 'workplaces-unclustered',
  ROUTE_LINE: 'route-line',
} as const

export const SOURCE_IDS = {
  BOUNDARIES: 'turf-boundaries',
  CONTACTS: 'contacts',
  WORKPLACES: 'workplaces',
  ROUTE: 'route',
} as const

/**
 * Color palette for turf visualization (cycling through 8 distinct colors)
 */
export const TURF_COLORS = [
  '#3B82F6', // blue
  '#EF4444', // red
  '#10B981', // green
  '#F59E0B', // amber
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#14B8A6', // teal
  '#F97316', // orange
] as const

/**
 * Get color for turf by index (1-based)
 */
export function getTurfColor(turfIndex: number): string {
  return TURF_COLORS[(turfIndex - 1) % TURF_COLORS.length]
}

/**
 * Household marker colors based on visit status
 */
export const HOUSEHOLD_COLORS = {
  all_visited: '#10B981', // green
  none_visited: '#EF4444', // red
  partial_visited: '#F59E0B', // yellow
  default: '#6B7280', // gray
} as const

/**
 * Workplace marker colors based on visit status
 */
export const WORKPLACE_COLORS = {
  all_visited: '#10B981', // green
  none_visited: '#3B82F6', // blue
  partial_visited: '#818CF8', // indigo
  needs_revisit: '#F59E0B', // yellow — visited but has NoAnswer/Callback contacts
  default: '#6B7280', // gray
} as const
